import { BRAND } from './brand';
import { DEFAULT_PRIMARY_CTA, utmCta } from './shared-ctas';
import type { PageCopy } from './page-copy-types';

// ─── /partners ────────────────────────────────────────────────────────────
export const PARTNERS_COPY: PageCopy = {
  eyebrow: 'Partner Program',
  h1: 'Your clients are losing calls. Help them - earn $50/mo.',
  lede:
    'Small businesses miss 50–70% of inbound calls. Every missed call is a lost customer. Refer your clients to BizRnR - they stop losing leads, you earn $50/month recurring per referral.',
  // canonical-divergence: partner program page targets advisors, not end-customers — keep "Apply as Partner".
  ctaPrimary: { label: 'Apply as Partner', href: '#partner-signup' },
  sections: [
    { heading: 'Built for advisors who touch small business clients' },
    { heading: 'How the partner program works' },
    { heading: 'Apply as a Partner' },
  ],
  metadata: {
    title: 'BizRnR Partner Program',
    description: 'Refer your small-business clients to BizRnR and earn $50/month per referral, recurring.',
    canonical: 'https://bizrnr.com/partners',
  },
};

// ─── /press ───────────────────────────────────────────────────────────────
export const PRESS_COPY: PageCopy = {
  eyebrow: 'Press Kit',
  h1: 'BizRnR press kit',
  lede:
    'Ready for journalists, podcast hosts, and analysts. Everything below is approved for use. Direct quotes, stats, logos, and founder bio.',
  // canonical-divergence: press kit primary action is to email the press contact, not start signup.
  ctaPrimary: { label: 'Email press@bizrnr.com', href: 'mailto:press@bizrnr.com' },
  sections: [
    { heading: 'One-line description' },
    { heading: 'Boilerplate' },
    { heading: 'Key stats (approved)' },
    { heading: 'Approved founder quotes' },
    { heading: 'Founder bio' },
    { heading: 'Logo + image assets' },
    { heading: 'Case studies' },
    { heading: 'Interview availability' },
    { heading: 'Press contact' },
  ],
  metadata: {
    title: 'BizRnR Press Kit — Logos, Quotes, Stats, Founder Bio',
    description:
      'Press kit for BizRnR — the AI voice agent for small business. Logos, stats, founder bio, boilerplate, approved quotes. Ready for journalists, podcast hosts, and analysts.',
    canonical: 'https://bizrnr.com/press',
  },
};

// ─── /lp/ai-discovery ─────────────────────────────────────────────────────
export const LP_AI_DISCOVERY_COPY: PageCopy = {
  eyebrow: 'AI Discovery Platform',
  h1: 'Your Business, Found by AI',
  lede:
    'ChatGPT, Google AI, Siri, and Alexa are changing local search. BizRnR gives your business a crawlable, structured AI-discovery page — starting at $99/month.',
  ctaPrimary: utmCta({
    label: `${BRAND.primaryCtaLabel} — get found by AI today`,
    source: 'paid',
    campaign: 'ai-discovery',
  }),
  sections: [
    { heading: 'Three steps to AI discovery', body: 'No code, no plugins, no technical skills. Set up in minutes, get discovered by AI in hours.' },
    { heading: 'AI is replacing traditional search', eyebrow: 'The Shift Is Happening Now' },
    { heading: 'Frequently asked questions' },
  ],
  faqs: [
    { q: 'What is AI-driven discovery?', a: 'AI-driven discovery is how modern consumers find businesses. Instead of typing keywords into Google, people ask ChatGPT, Siri, Alexa, or Google AI Overviews for recommendations. These AI systems read structured data, context pages, and machine-readable files to decide which businesses to recommend. BizRnR ensures your business is formatted and optimized so AI assistants can find you and recommend you to potential customers.' },
    { q: 'How does BizRnR get my business found by ChatGPT?', a: 'BizRnR creates an AI-optimized business page with structured data (JSON-LD), SpeakableSpecification markup, and an llms.txt file — the three signals AI crawlers look for. When ChatGPT, Perplexity, or Google AI Overviews scan the web for local business recommendations, your BizRnR page provides the exact machine-readable format they need to confidently recommend your business.' },
    { q: 'What is SpeakableSpecification?', a: 'SpeakableSpecification is a Schema.org markup that tells voice assistants like Siri, Alexa, and Google Assistant which parts of your page to read aloud. Without it, voice AI has to guess what to say about your business — and usually says nothing. BizRnR adds SpeakableSpecification to your business page automatically, so when someone asks a voice assistant about your industry in your area, your business description, services, and contact info are ready to be spoken.' },
    { q: 'How long does it take to appear in AI results?', a: 'Your BizRnR page goes live immediately and is submitted through supported discovery channels such as IndexNow/Bing, with sitemap and llms.txt context kept current. AI systems like ChatGPT update their web indexes and retrieval systems on different schedules, so inclusion and timing vary by platform.' },
    { q: 'Do I need any technical knowledge?', a: 'No. BizRnR handles all the technical work — structured data, SpeakableSpecification, llms.txt files, search engine submissions, and schema validation. You just sign up, answer a few questions about your business, and your AI-optimized page goes live. There is nothing to install, no code to write, and no plugins to configure.' },
    { q: 'What industries does BizRnR support?', a: 'BizRnR supports over 30 industries including HVAC, plumbing, electrical, dental, legal, real estate, insurance, solar, roofing, landscaping, auto repair, veterinary, medical practices, salons, restaurants, and more. Our AI voice agent and discovery platform are designed for any local service business that relies on phone calls and appointments to generate revenue.' },
    { q: 'Can I keep my existing website?', a: 'Absolutely. BizRnR does not replace your website — it supplements it. Your BizRnR page is a separate, AI-optimized presence that works alongside your existing site. Think of it as a dedicated channel for AI discovery, similar to how you might have a Google Business Profile alongside your website. Many customers link their BizRnR page from their main site to strengthen both.' },
    { q: 'How does the 30-day guarantee work?', a: 'Start AI Receptionist for $99/month. If BizRnR is not the right fit, cancel within 30 days and we will refund the subscription charge. No long-term contract, no cancellation fees, and your data is yours.' },
    { q: 'What is llms.txt and why does it matter?', a: 'llms.txt is a machine-readable text file (similar to robots.txt) that gives large language models structured context about a business. When AI systems like ChatGPT, Claude, or Perplexity crawl the web, they look for llms.txt files to quickly understand what a business does, who it serves, and how to contact them. BizRnR generates and maintains your llms.txt automatically.' },
    { q: 'How is this different from SEO?', a: 'Traditional SEO optimizes your website for Google keyword search — meta tags, backlinks, page speed. AI discovery optimization (AEO) prepares your business for a fundamentally different system: AI assistants that read structured data and generate answers, not links. SEO gets you ranked in a list of ten blue links. AEO gets you recommended by name when someone asks an AI assistant for help. You need both, and BizRnR handles the AEO side.' },
  ],
  metadata: {
    title: 'AI Discovery Platform — Get Found by ChatGPT, Google AI, Siri & Alexa | BizRnR',
    description:
      'BizRnR gives your business AI-readable structured data, SpeakableSpecification, sitemap coverage, and llms.txt context for ChatGPT, Google AI Overviews, Siri, and Alexa. Starting at $99/mo.',
    canonical: 'https://bizrnr.com/lp/ai-discovery',
  },
};

// ─── /guides/ai-voice-agent-for-small-business ────────────────────────────
export const GUIDES_AI_VOICE_AGENT_COPY: PageCopy = {
  h1: 'The Ultimate Guide to AI Voice Agents for Small Business (2026)',
  lede: `A complete reference for solopreneurs and small-business owners evaluating AI voice agents in 2026. What they are, how they work, how much they cost, and how to deploy one in under ${BRAND.setupTimeClaim}.`,
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    { heading: 'What is an AI voice agent?' },
    { heading: 'Why small businesses need one in 2026' },
    { heading: 'How AI voice agents work' },
    { heading: 'Pricing models' },
    { heading: 'Deployment & setup' },
    { heading: 'Use cases by vertical' },
    { heading: 'How to choose one' },
  ],
  metadata: {
    title: 'The Ultimate Guide to AI Voice Agents for Small Business (2026)',
    description:
      'The complete 2026 guide to AI voice agents for small business — what they are, how they work, how much they cost, how fast they deploy, and how to pick the right one across HVAC, dental, real estate, legal, and 16 more verticals.',
    canonical: 'https://bizrnr.com/guides/ai-voice-agent-for-small-business',
  },
};

// ─── /status ──────────────────────────────────────────────────────────────
export const STATUS_COPY: PageCopy = {
  eyebrow: 'BizRnR platform status',
  h1: 'All systems operational',
  lede: 'Live status snapshot · refreshed automatically',
  // canonical-divergence: status page CTA returns to homepage — utility page, not lead-funnel.
  ctaPrimary: { label: 'Back to home', href: '/' },
  sections: [
    { heading: 'Services' },
    { heading: 'Live snapshot', body: 'Real-time availability of each core service.' },
    { heading: 'Data source: live platform health checks, refreshed automatically. All times UTC.' },
  ],
  metadata: {
    title: 'BizRnR Platform Status',
    description: 'Live platform status snapshot — real-time availability per service, refreshed automatically.',
    canonical: 'https://bizrnr.com/status',
  },
};

// ─── /subscription-expired ────────────────────────────────────────────────
export const SUBSCRIPTION_EXPIRED_COPY: PageCopy = {
  h1: 'Subscription Expired',
  lede:
    'Your BizRnR subscription has expired. Renew to continue using your AI phone agents, CRM, and automation tools.',
  // canonical-divergence: existing-customer renewal flow, not new-customer signup.
  ctaPrimary: { label: 'Renew Subscription', href: '/pricing' },
  ctaSecondary: { label: 'Sign out', href: '/' },
  sections: [
    {
      heading: 'What you’re missing:',
      bullets: [
        'AI-powered inbound & outbound calls',
        'Contact management & batch calling',
        'SMS messaging & appointment scheduling',
        'Call recordings & transcripts',
      ],
    },
    { heading: 'Your access has been suspended' },
  ],
  metadata: {
    title: 'Subscription Expired — BizRnR',
    description: 'Your BizRnR subscription has expired. Renew to continue.',
    canonical: 'https://bizrnr.com/subscription-expired',
  },
};

// ─── /l/[slug] (custom-landing) ───────────────────────────────────────────
// dynamic content lives in custom_landing_pages Supabase table (page_content.headline,
// page_content.subheadline, page_content.bullets, page_content.cta_text);
// fallback shell rendered when content is still pending
export const L_SLUG_COPY: PageCopy = {
  h1: 'Your custom page is almost ready',
  lede:
    'We’re tailoring the copy right now. Refresh in a few seconds, or tap the button below to get started with AI Receptionist.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    {
      heading: 'What BizRnR delivers',
      bullets: [
        'Every call answered on the first ring — 24/7/365',
        'Leads qualified and appointments booked to your calendar',
        'Missed callers get an instant SMS — nobody walks to your competitor',
      ],
    },
  ],
  metadata: {
    title: 'BizRnR — Personalized landing',
    description: 'Personalized landing page generated from a Bri qualification call.',
    canonical: 'https://bizrnr.com/l/{slug}',
  },
};
